import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, Pause, Play, Trophy } from 'lucide-react';
import { api } from '@/lib/api';
import { usePlayer } from '@/context/PlayerContext';
import { ChallengeCard } from '@/components/cards';
import { SectionHead } from '@/components/landing/Sections';

function EntryRow({ e, rank, queue }) {
  const { playTrack, current, playing, toggle } = usePlayer();
  const isCurrent = current?.song_id === e.song_id;
  const name = e.creator?.display_name || e.creator?.username || 'Créateur';
  return (
    <div className="flex items-center gap-4 rounded-2xl p-3 transition-colors hover:bg-white/5" data-testid={`discover-entry-${e.song_id}`}>
      <span className="w-6 text-center font-mono text-sm text-white/40">{rank}</span>
      <button onClick={() => (isCurrent ? toggle() : playTrack(queue[rank - 1], queue))} data-testid={`discover-play-${e.song_id}`}
              aria-label={`Écouter ${e.title}`} className="relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-xl">
        {e.cover_url && <img src={e.cover_url} alt="" loading="lazy" className="h-full w-full object-cover" />}
        <span className="absolute inset-0 flex items-center justify-center bg-black/45">
          {isCurrent && playing ? <Pause className="h-5 w-5 text-white" /> : <Play className="h-5 w-5 fill-white text-white" />}
        </span>
      </button>
      <div className="min-w-0 flex-1">
        <p className="truncate font-display font-semibold text-white">{e.title}</p>
        <p className="truncate text-xs text-white/50">{name}</p>
      </div>
      <span className="flex items-center gap-1 font-mono text-xs text-white/60"><Heart className="h-3.5 w-3.5 text-brand" />{e.likes ?? 0}</span>
    </div>
  );
}

export function Discover() {
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState([]);
  const [top, setTop] = useState({ challenge: null, entries: [] });

  useEffect(() => {
    api.get('/challenges').then(async ({ data }) => {
      const active = (data || []).filter((c) => c.status === 'active');
      setChallenges(active.slice(0, 3));
      const pick = active.find((c) => (c.entries_count || 0) > 0);
      if (!pick) return;
      const { data: d } = await api.get(`/challenges/${pick.slug}`);
      const entries = [...(d.entries || [])].sort((a, b) => (b.likes || 0) - (a.likes || 0)).slice(0, 5);
      setTop({ challenge: d, entries });
    }).catch(() => {});
  }, []);

  if (!challenges.length) return null;
  const queue = top.entries.map((e) => ({ song_id: e.song_id, title: e.title, slug: e.slug, cover_url: e.cover_url, audio_url: e.audio_url,
    style_text: e.style_text, duration_seconds: e.duration_seconds, creator: e.creator?.display_name || e.creator?.username }));

  return (
    <section id="decouverte" className="scroll-mt-24 py-24" data-testid="landing-discover">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <SectionHead kicker="Découverte musicale" title="La communauté crée, tu écoutes."
                     text="Uniquement des chansons publiées volontairement par leurs créateurs dans les challenges." />
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.2fr_1fr] [&>*]:min-w-0">
          <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            {challenges.map((c) => <ChallengeCard key={c.id} challenge={c} onClick={() => navigate(`/challenges/${c.slug}`)} />)}
          </div>
          {top.entries.length > 0 && (
            <div className="card-surface p-5" data-testid="discover-top-entries">
              <p className="flex items-center gap-2 px-3 pb-3 font-display font-bold text-white"><Trophy className="h-5 w-5 text-brand" /> Top — {top.challenge.title}</p>
              {top.entries.map((e, i) => <EntryRow key={e.entry_id} e={e} rank={i + 1} queue={queue} />)}
              <Link to={`/challenges/${top.challenge.slug}`} className="mt-3 block px-3 text-sm text-brand hover:underline">Voir tout le classement →</Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
